import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Filter, Pencil, Trash2, Eye, FileText, PlusCircle, Loader2, X, Download, FileSpreadsheet } from "lucide-react";
import api, { apiError, fmtTRY, fmtDate, PAYMENT_METHODS, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const emptyFilters = {
  trade_id: "", supplier: "", user_id: "", payment_method: "",
  date_from: "", date_to: "", min_amount: "", max_amount: "",
};

function AttachmentLink({ att }) {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/files/${att.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Dosya açılamadı");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button onClick={open} className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-secondary" data-testid={`attachment-${att.id}`}>
      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="flex-1 truncate">{att.original_filename}</span>
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-slate-400" />}
    </button>
  );
}

export default function Expenses() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [trades, setTrades] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);

  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = { search: search || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/expenses", { params });
      setExpenses(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/trades").then((r) => setTrades(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  const total = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = { search: search || undefined };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/export/excel", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `santiye_giderler_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader overline="Kayıtlar" title="Giderler" subtitle={`${expenses.length} kayıt · Toplam ${fmtTRY(total)}`}>
        <Button variant="outline" onClick={exportExcel} disabled={exporting || expenses.length === 0} data-testid="export-excel-button">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Excel'e Aktar
        </Button>
        <Button onClick={() => navigate("/expenses/new")} data-testid="expenses-new-button">
          <PlusCircle className="mr-2 h-4 w-4" /> Yeni Gider
        </Button>
      </PageHeader>

      {/* Search + filter toggle */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Açıklama, tedarikçi, iş kalemi veya notlarda ara..."
            className="pl-9"
            data-testid="expenses-search-input"
          />
        </div>
        <Button variant="outline" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-button">
          <Filter className="mr-2 h-4 w-4" /> Filtreler
          {activeFilterCount > 0 && <Badge className="ml-2" variant="secondary">{activeFilterCount}</Badge>}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="filters-panel">
          <div className="space-y-1.5">
            <Label className="text-xs">İş Kalemi</Label>
            <Select value={filters.trade_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, trade_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-trade"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kullanıcı</Label>
            <Select value={filters.user_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, user_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Ödeme Yöntemi</Label>
            <Select value={filters.payment_method || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, payment_method: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="filter-payment"><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Tedarikçi</Label>
            <Input value={filters.supplier} onChange={(e) => setFilters((f) => ({ ...f, supplier: e.target.value }))} placeholder="Tedarikçi" data-testid="filter-supplier" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Başlangıç Tarihi</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Bitiş Tarihi</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Min. Tutar</Label>
            <Input type="number" value={filters.min_amount} onChange={(e) => setFilters((f) => ({ ...f, min_amount: e.target.value }))} placeholder="0" data-testid="filter-min" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Maks. Tutar</Label>
            <Input type="number" value={filters.max_amount} onChange={(e) => setFilters((f) => ({ ...f, max_amount: e.target.value }))} placeholder="∞" data-testid="filter-max" />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-4">
            <Button variant="ghost" onClick={() => setFilters(emptyFilters)} data-testid="clear-filters-button">
              <X className="mr-1 h-4 w-4" /> Filtreleri Temizle
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="overflow-x-auto">
          <Table data-testid="expenses-table">
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Tarih</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>İş Kalemi</TableHead>
                <TableHead>Tedarikçi</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
              ) : expenses.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>
              ) : (
                expenses.map((e) => (
                  <TableRow key={e.id} className="transition-colors hover:bg-secondary/40" data-testid={`expense-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">{fmtDate(e.expense_date)}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {e.description}
                        {e.attachments?.length > 0 && <FileText className="h-3.5 w-3.5 text-slate-400" />}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{e.trade_name}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{e.supplier}</TableCell>
                    <TableCell className="text-muted-foreground">{e.created_by}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{fmtTRY(e.amount)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(e)} data-testid={`view-${e.id}`}><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(e)} data-testid={`edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleting(e)} data-testid={`delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {detail && <DetailDialog expense={detail} onClose={() => setDetail(null)} />}
      {editing && <EditDialog expense={editing} trades={trades} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <DeleteDialog expense={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function DetailDialog({ expense, onClose }) {
  const rows = [
    ["Gider Tarihi", fmtDate(expense.expense_date)],
    ["Kayıt Tarihi", fmtDate(expense.entry_date)],
    ["Tutar", fmtTRY(expense.amount)],
    ["Açıklama", expense.description],
    ["İş Kalemi", expense.trade_name],
    ["Tedarikçi / Lehtar", expense.supplier],
    ["Ödeme Yöntemi", expense.payment_method],
    ["Kaydeden", expense.created_by],
    ["Gözlemler", expense.observations || "—"],
  ];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="expense-detail-dialog">
        <DialogHeader><DialogTitle>Gider Detayı</DialogTitle></DialogHeader>
        <div className="divide-y divide-border text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 py-2">
              <span className="text-muted-foreground">{k}</span>
              <span className="text-right font-medium">{v}</span>
            </div>
          ))}
        </div>
        {expense.attachments?.length > 0 && (
          <div className="space-y-2">
            <p className="overline">Ekler</p>
            {expense.attachments.map((a) => <AttachmentLink key={a.id} att={a} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ expense, trades, onClose, onSaved }) {
  const [form, setForm] = useState({
    expense_date: expense.expense_date, amount: String(expense.amount), description: expense.description,
    trade_id: expense.trade_id, supplier: expense.supplier, payment_method: expense.payment_method,
    observations: expense.observations || "",
  });
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!comment.trim()) { toast.error("Değişiklik yorumu zorunludur"); return; }
    setSaving(true);
    try {
      await api.put(`/expenses/${expense.id}`, { ...form, amount: Number(form.amount), comment: comment.trim() });
      toast.success("Gider güncellendi");
      onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="expense-edit-dialog">
        <DialogHeader>
          <DialogTitle>Gideri Düzenle</DialogTitle>
          <DialogDescription>Değişiklik için zorunlu bir yorum giriniz. Eski değerler geçmişe kaydedilir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tarih</Label><Input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)} data-testid="edit-date" /></div>
            <div className="space-y-1.5"><Label>Tutar</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="edit-amount" /></div>
          </div>
          <div className="space-y-1.5"><Label>Açıklama</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>İş Kalemi</Label>
              <Select value={form.trade_id} onValueChange={(v) => set("trade_id", v)}>
                <SelectTrigger data-testid="edit-trade"><SelectValue /></SelectTrigger>
                <SelectContent>{trades.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ödeme</Label>
              <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                <SelectTrigger data-testid="edit-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Tedarikçi</Label><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="edit-supplier" /></div>
          <div className="space-y-1.5"><Label>Gözlemler</Label><Textarea rows={2} value={form.observations} onChange={(e) => set("observations", e.target.value)} data-testid="edit-observations" /></div>
          <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-3">
            <Label className="text-amber-900">Değişiklik Yorumu * (zorunlu)</Label>
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Örn: Fatura tutarı düzeltildi" data-testid="edit-comment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ expense, onClose, onDeleted }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const del = async () => {
    if (!comment.trim()) { toast.error("Silme yorumu zorunludur"); return; }
    setSaving(true);
    try {
      await api.delete(`/expenses/${expense.id}`, { data: { comment: comment.trim() } });
      toast.success("Gider arşivlendi");
      onDeleted();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <AlertDialog open onOpenChange={onClose}>
      <AlertDialogContent data-testid="expense-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Gideri sil?</AlertDialogTitle>
          <AlertDialogDescription>
            "{expense.description}" ({fmtTRY(expense.amount)}) arşivlenecek. Veri kalıcı olarak silinmez, geçmişte saklanır. Devam etmek için bir yorum giriniz.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5 rounded-md border border-red-200 bg-red-50 p-3">
          <Label className="text-red-900">Silme Yorumu * (zorunlu)</Label>
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Örn: Hatalı kayıt" data-testid="delete-comment" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel-button">Vazgeç</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); del(); }} disabled={saving} className="bg-destructive hover:bg-destructive/90" data-testid="delete-confirm-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sil"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
